// A brand-new generative soundscape (Web Audio API, no audio files) — nothing
// reused from prior versions. Identity: FM-synthesized crystalline bells playing a
// slowly wandering pentatonic, cascading through a ping-pong dub delay, over a
// breathing band-passed "wind" bed. No drones, drums, 808s, binaural or strings.
// Starts only after a user gesture (autoplay policy).

const MODES = {
  veil: {
    scale: [440.0, 523.25, 587.33, 659.25, 783.99, 880.0], // A minor pentatonic (high)
    stepSec: 0.42,
    trigProb: 0.62,
    ratio: 3.5, // FM modulator:carrier (glassy/metallic)
    idx: 2.6, // FM index (brightness)
    bellDecay: 1.8,
    windBP: 900,
    padCarrier: 110.0,
    padRatio: 2.0,
    delay: 0.38,
    fb: 0.42,
    level: 0.3,
  },
  sanctum: {
    scale: [220.0, 261.63, 293.66, 329.63, 392.0, 440.0], // one octave lower, warmer
    stepSec: 0.62,
    trigProb: 0.5,
    ratio: 2.0,
    idx: 2.0,
    bellDecay: 2.6,
    windBP: 520,
    padCarrier: 82.41,
    padRatio: 1.5,
    delay: 0.52,
    fb: 0.5,
    level: 0.32,
  },
};

function makeReverbImpulse(ctx, seconds = 3.4, decay = 2.4) {
  const rate = ctx.sampleRate;
  const len = Math.floor(rate * seconds);
  const impulse = ctx.createBuffer(2, len, rate);
  for (let ch = 0; ch < 2; ch++) {
    const data = impulse.getChannelData(ch);
    for (let i = 0; i < len; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
    }
  }
  return impulse;
}

function makeNoiseBuffer(ctx, seconds = 2) {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

export function createRitualAudio() {
  let ctx = null;
  let master = null;
  let reverb = null;
  let bellBus = null;
  let delayL = null;
  let delayR = null;
  let refs = {};
  let sustained = [];
  let noiseBuf = null;
  let schedTimer = null;
  let nextNoteTime = 0;
  let walk = 2;
  let started = false;
  let muted = false;
  let mode = "veil";

  const playBell = (t, freq) => {
    const m = MODES[mode];
    const car = ctx.createOscillator();
    car.type = "sine";
    car.frequency.value = freq;
    const modu = ctx.createOscillator();
    modu.type = "sine";
    modu.frequency.value = freq * m.ratio;
    const modGain = ctx.createGain();
    modGain.gain.setValueAtTime(freq * m.idx, t);
    modGain.gain.exponentialRampToValueAtTime(1, t + m.bellDecay * 0.85);
    modu.connect(modGain).connect(car.frequency);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(0.5, t + 0.006);
    g.gain.exponentialRampToValueAtTime(0.001, t + m.bellDecay);
    car.connect(g).connect(bellBus);
    modu.start(t);
    car.start(t);
    modu.stop(t + m.bellDecay + 0.1);
    car.stop(t + m.bellDecay + 0.1);
  };

  const scheduler = () => {
    if (!ctx) return;
    while (nextNoteTime < ctx.currentTime + 0.25) {
      const m = MODES[mode];
      if (Math.random() < m.trigProb) {
        walk = clamp(walk + (Math.random() < 0.5 ? -1 : 1), 0, m.scale.length - 1);
        playBell(nextNoteTime, m.scale[walk]);
      }
      nextNoteTime += MODES[mode].stepSec;
    }
  };

  const start = async () => {
    if (started) {
      if (ctx && ctx.state === "suspended") await ctx.resume();
      return;
    }
    started = true;
    const AC = window.AudioContext || window.webkitAudioContext;
    ctx = new AC();
    if (ctx.state === "suspended") await ctx.resume();
    noiseBuf = makeNoiseBuffer(ctx);

    const m = MODES[mode];
    master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, ctx.currentTime);
    master.gain.exponentialRampToValueAtTime(m.level, ctx.currentTime + 4);
    master.connect(ctx.destination);

    reverb = ctx.createConvolver();
    reverb.buffer = makeReverbImpulse(ctx);
    const reverbGain = ctx.createGain();
    reverbGain.gain.value = 0.95;
    reverb.connect(reverbGain).connect(master);

    // Bell bus -> dry + reverb + ping-pong delay
    bellBus = ctx.createGain();
    bellBus.gain.value = 0.6;
    bellBus.connect(master);
    bellBus.connect(reverb);

    delayL = ctx.createDelay(1.5);
    delayR = ctx.createDelay(1.5);
    delayL.delayTime.value = m.delay;
    delayR.delayTime.value = m.delay * 1.5;
    const fbL = ctx.createGain();
    const fbR = ctx.createGain();
    fbL.gain.value = m.fb;
    fbR.gain.value = m.fb;
    const panDL = ctx.createStereoPanner();
    panDL.pan.value = -0.75;
    const panDR = ctx.createStereoPanner();
    panDR.pan.value = 0.75;
    // cross-feedback ping-pong
    delayL.connect(fbL).connect(delayR);
    delayR.connect(fbR).connect(delayL);
    delayL.connect(panDL).connect(master);
    delayR.connect(panDR).connect(master);
    delayL.connect(reverb);
    bellBus.connect(delayL);

    // Breathing wind bed — band-passed noise, two panned voices with LFO sweeps
    const windGain = ctx.createGain();
    windGain.gain.value = 0.06;
    windGain.connect(master);
    windGain.connect(reverb);
    const windBPs = [];
    [-0.6, 0.6].forEach((pan, i) => {
      const src = ctx.createBufferSource();
      src.buffer = noiseBuf;
      src.loop = true;
      const bp = ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = m.windBP;
      bp.Q.value = 3.5;
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 0.05 + i * 0.03;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = m.windBP * 0.5;
      lfo.connect(lfoGain).connect(bp.frequency);
      const p = ctx.createStereoPanner();
      p.pan.value = pan;
      src.connect(bp).connect(p).connect(windGain);
      src.start();
      lfo.start();
      sustained.push(src, lfo);
      windBPs.push(bp);
    });

    // Low FM pad (evolving warmth) — sustained
    const padCar = ctx.createOscillator();
    padCar.type = "sine";
    padCar.frequency.value = m.padCarrier;
    const padMod = ctx.createOscillator();
    padMod.type = "sine";
    padMod.frequency.value = m.padCarrier * m.padRatio;
    const padModGain = ctx.createGain();
    padModGain.gain.value = m.padCarrier * 1.2;
    const padIdxLFO = ctx.createOscillator();
    padIdxLFO.frequency.value = 0.07;
    const padIdxLFOGain = ctx.createGain();
    padIdxLFOGain.gain.value = m.padCarrier * 0.8;
    padIdxLFO.connect(padIdxLFOGain).connect(padModGain.gain);
    padMod.connect(padModGain).connect(padCar.frequency);
    const padGain = ctx.createGain();
    padGain.gain.value = 0.07;
    const padLP = ctx.createBiquadFilter();
    padLP.type = "lowpass";
    padLP.frequency.value = 700;
    padCar.connect(padLP).connect(padGain).connect(master);
    padGain.connect(reverb);
    padCar.start();
    padMod.start();
    padIdxLFO.start();
    sustained.push(padCar, padMod, padIdxLFO);

    refs = { padCar, padMod, windBPs, fbL, fbR };

    walk = 2;
    nextNoteTime = ctx.currentTime + 0.2;
    schedTimer = setInterval(scheduler, 25);
  };

  const setMode = (next) => {
    if (!MODES[next]) return;
    mode = next;
    if (!ctx || !master) return;
    const m = MODES[mode];
    const t = ctx.currentTime;
    const g = 3.5;
    if (refs.padCar) refs.padCar.frequency.linearRampToValueAtTime(m.padCarrier, t + g);
    if (refs.padMod) refs.padMod.frequency.linearRampToValueAtTime(m.padCarrier * m.padRatio, t + g);
    if (refs.windBPs) refs.windBPs.forEach((bp) => bp.frequency.linearRampToValueAtTime(m.windBP, t + g));
    if (refs.fbL) refs.fbL.gain.linearRampToValueAtTime(m.fb, t + g);
    if (refs.fbR) refs.fbR.gain.linearRampToValueAtTime(m.fb, t + g);
    if (delayL) delayL.delayTime.linearRampToValueAtTime(m.delay, t + g);
    if (delayR) delayR.delayTime.linearRampToValueAtTime(m.delay * 1.5, t + g);
    if (!muted) master.gain.exponentialRampToValueAtTime(m.level, t + g);
  };

  const setMuted = (mtd) => {
    muted = mtd;
    if (!ctx || !master) return;
    const t = ctx.currentTime;
    master.gain.cancelScheduledValues(t);
    master.gain.setValueAtTime(Math.max(0.0001, master.gain.value), t);
    master.gain.exponentialRampToValueAtTime(mtd ? 0.0001 : MODES[mode].level, t + 0.6);
  };

  const stop = () => {
    if (schedTimer) clearInterval(schedTimer);
    sustained.forEach((n) => {
      try {
        n.stop();
      } catch (e) {}
    });
    sustained = [];
    if (ctx) {
      try {
        ctx.close();
      } catch (e) {}
    }
    ctx = null;
    started = false;
  };

  return {
    start,
    stop,
    setMuted,
    setMode,
    isStarted: () => started,
    isMuted: () => muted,
    getMode: () => mode,
  };
}
